import React, {ReactNode} from 'react';
import moment, {Moment} from 'moment';
import _ from 'lodash';
import {classes, stylesheet} from 'typestyle';
import {observer} from 'mobx-react';
import {LineChart as RechartsLineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ReferenceArea} from 'recharts';

import TooltipContent, {ChartType} from './TooltipContent';
import {BaseChartData, ChartProps, KpiDataPackage, VisibleAttribute} from '../../types/Chart';
import {DateRange} from '../../stores/TimeIntervalStore';
import {formatValue, percentFormat} from '../../utility/FormatValueUtils';
import {colorToString} from '../../utility/ColorUtils';
import {GreyColor, DarkGreyColor} from '../../styles/colors';

type RechartsLineChartProps = typeof RechartsLineChart['defaultProps'];

interface LineChartState {
    xAxisRangePositions: XAxisRangePositions;
    startDragTimestamp: number | null;
    endDragTimestamp: number | null;
    topEndPosition: string | number;
}

export interface LineChartData extends BaseChartData {
    timestamp?: number;
    date: Moment;
    testId?: string;
    variantId?: string;
}

interface LineChartProps extends ChartProps {
    data: LineChartData[];
    mainDateRange: DateRange;
    compareDateRange: DateRange;
    currencyFormatting?: (value: number) => string;
    disableTooltip?: boolean;
    disableClickableDot?: boolean;
}

interface XAxisRange {
    start: string | number;
    end: string | number;
}

interface XAxisRangePositions {
    x1Range: XAxisRange;
    x2Range: XAxisRange;
}

const LINE_CHART_SIDE_PADDING = 85;

@observer
export default class LineChart extends React.Component<LineChartProps, LineChartState> {
    constructor(props: LineChartProps) {
        super(props);
        this.state = {
            xAxisRangePositions: this.getXAxisRangePositions(props.data),
            startDragTimestamp: null,
            endDragTimestamp: null,
            topEndPosition: 'dataMax',
        };
    }

    private visibleAttributesChanged(nextVisibleAttribute: VisibleAttribute[]): boolean {
        if (nextVisibleAttribute.length !== this.props.visibleAttribute.length) {
            return true;
        }
        return nextVisibleAttribute.some((singleNextVisibleAttribute, index) => {
            const singleCurrentVisibleAttribute = this.props.visibleAttribute[index];
            if (!singleCurrentVisibleAttribute) {
                return true;
            }
            return !_.isEqual(singleCurrentVisibleAttribute, singleNextVisibleAttribute);
        });
    }

    public componentWillReceiveProps(nextProps: LineChartProps) {
        const nextDefaultXAxisPositions = this.getXAxisRangePositions(nextProps.data);
        const currentDefaultXAxisPositions = this.getXAxisRangePositions(this.props.data);
        if (
            nextDefaultXAxisPositions.x1Range.start !== currentDefaultXAxisPositions.x1Range.start ||
            nextDefaultXAxisPositions.x1Range.end !== currentDefaultXAxisPositions.x1Range.end ||
            nextDefaultXAxisPositions.x2Range.start !== currentDefaultXAxisPositions.x2Range.start ||
            nextDefaultXAxisPositions.x2Range.end !== currentDefaultXAxisPositions.x2Range.end ||
            this.visibleAttributesChanged(nextProps.visibleAttribute)
        ) {
            this.setState({
                xAxisRangePositions: nextDefaultXAxisPositions,
            });
        }
    }

    private getDateRangeFromData(data: LineChartData[], compare: (kpiDataPackage: KpiDataPackage) => boolean): DateRange {
        let startDate: Moment | undefined;
        let endDate: Moment | undefined;
        const dateRangeEntries = data
            .filter((singleDataGroup) => singleDataGroup.data.some(compare))
            .sort((a, b) => a.date.valueOf() - b.date.valueOf());
        if (dateRangeEntries.length > 1) {
            startDate = dateRangeEntries[0].date;
            endDate = dateRangeEntries[dateRangeEntries.length - 1].date;
        }
        return {startDate, endDate};
    }

    private getXAxisRangePositions(data: LineChartData[]): XAxisRangePositions {
        const {startDate, endDate} = this.getDateRangeFromData(data, (dataInGroup) => !dataInGroup.isCompareDiagram);
        const {startDate: compareStartDate, endDate: compareEndDate} = this.getDateRangeFromData(data, (dataInGroup) => dataInGroup.isCompareDiagram);

        return {
            x1Range: {
                start: startDate ? startDate.valueOf() : 'dataMin',
                end: endDate ? endDate.valueOf() : 'dataMax',
            },
            x2Range: {
                start: compareStartDate ? compareStartDate.valueOf() : 'dataMin',
                end: compareEndDate ? compareEndDate.valueOf() : 'dataMax',
            },
        };
    }

    private zoomOut(): void {
        this.setState(() => ({
            startDragTimestamp: null,
            endDragTimestamp: null,
            xAxisRangePositions: this.getXAxisRangePositions(this.props.data),
            topEndPosition: 'dataMax',
        }));
    }

    private getData(): LineChartData[] {
        return this.props.data.map(
            (diagramData: LineChartData): LineChartData => {
                return {
                    timestamp: diagramData.date.valueOf(),
                    ...diagramData,
                };
            }
        );
    }

    private renderLines(useAbsValue: boolean): React.ReactNode[] {
        return this.props.visibleAttribute.map((lineAttribute) => {
            const {channel, kpi, searchtermOrProductId, isCompareDiagram, isChannelDiagram, isRate, testId, variantId} = lineAttribute.kpiRequestInfo;
            const allowAnimation = this.props.animation === true;
            const color = colorToString(lineAttribute.color);
            return (
                <Line
                    xAxisId={isCompareDiagram ? '2' : '1'}
                    yAxisId={isRate || !useAbsValue ? '2' : '1'}
                    type="linear"
                    dot={false}
                    activeDot={true}
                    dataKey={(singleEntry: LineChartData) => {
                        const dataSet = singleEntry.data.find(
                            (data) =>
                                data.channel.name === channel.name &&
                                data.searchtermOrProductId === searchtermOrProductId &&
                                data.kpi === kpi &&
                                data.isChannelDiagram === isChannelDiagram &&
                                data.isCompareDiagram === isCompareDiagram &&
                                data.testId === testId &&
                                data.variantId === variantId
                        );

                        if (dataSet !== undefined) {
                            if (lineAttribute.useAbsValue !== undefined && !lineAttribute.useAbsValue) {
                                return dataSet.relValue;
                            }
                            return dataSet.value;
                        }
                        return null;
                    }}
                    key={JSON.stringify(lineAttribute)}
                    strokeDasharray={lineAttribute.kpiRequestInfo.isCompareDiagram ? '5 5' : undefined}
                    strokeWidth={2}
                    stroke={color}
                    opacity={lineAttribute.opacity === undefined ? 1 : lineAttribute.opacity}
                    isAnimationActive={allowAnimation}
                    animationDuration={allowAnimation ? 300 : 0}
                />
            );
        });
    }

    private zoom = (): void => {
        let {startDragTimestamp, endDragTimestamp} = this.state;

        if (startDragTimestamp === null) {
            return;
        }

        if (endDragTimestamp === null || startDragTimestamp === endDragTimestamp) {
            this.zoomOut();
            return;
        }

        if (startDragTimestamp > endDragTimestamp) {
            [startDragTimestamp, endDragTimestamp] = [endDragTimestamp, startDragTimestamp];
        }

        const startDrag = moment(startDragTimestamp);
        const endDrag = moment(endDragTimestamp);

        const diff = endDrag.diff(startDrag, 'seconds');
        const startDiff = startDrag.diff(this.props.mainDateRange.startDate, 'seconds');

        const compareStartDrag = moment(this.props.compareDateRange.startDate).add(startDiff, 'seconds');
        const compareEndDrag = moment(compareStartDrag).add(diff, 'seconds');

        this.setState({
            startDragTimestamp: null,
            endDragTimestamp: null,
            xAxisRangePositions: {
                x1Range: {
                    start: startDragTimestamp,
                    end: endDragTimestamp,
                },
                x2Range: {
                    start: compareStartDrag.valueOf(),
                    end: compareEndDrag.valueOf(),
                },
            },
        });
    };

    private onDiagramMouseDown: RechartsLineChartProps['onMouseDown'] = (e): void => {
        if (e !== null) {
            this.setState({startDragTimestamp: e.activeLabel === undefined || e.activeLabel === '' ? null : parseInt(e.activeLabel, 10)});
        }
    };

    private onDiagramMouseMove: RechartsLineChartProps['onMouseMove'] = (e): void => {
        if (e !== null) {
            this.state.startDragTimestamp &&
                this.setState({endDragTimestamp: e.activeLabel === undefined || e.activeLabel === '' ? null : parseInt(e.activeLabel, 10)});
        }
    };

    private containsNoneCompareData(kpiPackages: KpiDataPackage[]): boolean {
        return kpiPackages.some((kpiPackage) => !kpiPackage.isCompareDiagram);
    }

    private containsCompareData(kpiPackages: KpiDataPackage[]): boolean {
        return kpiPackages.some((kpiPackage) => kpiPackage.isCompareDiagram);
    }

    private generateDailyTicks(xAxisId: number): number[] {
        if (xAxisId === 2) {
            return this.props.data
                .filter((data) => data.date.hour() === 0 && this.containsCompareData(data.data))
                .map((singleHourData) => singleHourData.date.valueOf());
        }

        return this.props.data
            .filter((data) => data.date.hour() === 0 && this.containsNoneCompareData(data.data))
            .map((singleHourData) => singleHourData.date.valueOf());
    }

    private formatXAxisTick = (timestamp: number) => {
        return moment(timestamp).format('dd') + ' ' + moment(timestamp, 'x').format('L');
    };

    private renderYAxis(useAbsValue: boolean, ratesAvailable: boolean): React.ReactNode[] {
        const yAxis: ReactNode[] = [];
        const {topEndPosition} = this.state;

        yAxis.push(
            <YAxis
                scale="linear"
                width={LINE_CHART_SIDE_PADDING}
                key="1"
                allowDataOverflow={true}
                tickFormatter={(tickValue: number) => {
                    return String(_.round(tickValue, 2));
                }}
                domain={[0, topEndPosition]}
                type="number"
                yAxisId="1"
            />
        );

        yAxis.push(
            <YAxis
                scale="linear"
                width={LINE_CHART_SIDE_PADDING}
                key="2"
                orientation="right"
                allowDataOverflow={true}
                tickFormatter={(tickValue: number) => {
                    return formatValue(tickValue, percentFormat());
                }}
                domain={[0, topEndPosition]}
                type="number"
                yAxisId="2"
            />
        );

        return yAxis;
    }

    private addNormalAndCompareLegenLines(compareDiagramUsed: boolean): React.ReactNode[] | null {
        if (!compareDiagramUsed) {
            return null;
        }

        return [
            <div className={lineChartCss.axisLinePreview} key={0}>
                {'───'}
            </div>,
            <div className={classes(lineChartCss.bottomLineTypeItem, lineChartCss.axisLinePreview)} key={1}>
                {'╌╌'}
            </div>,
        ];
    }

    private currencyFormatting() {
        return this.props.currencyFormatting ? this.props.currencyFormatting : (value: number) => value.toString();
    }

    public render(): React.ReactNode {
        const {visibleAttribute} = this.props;
        const {
            xAxisRangePositions: {x1Range, x2Range},
            startDragTimestamp,
            endDragTimestamp,
        } = this.state;

        const compareDiagramUsed = this.props.visibleAttribute.some(
            (singleVisibleAttribute) => singleVisibleAttribute.kpiRequestInfo.isCompareDiagram
        );

        const ratesAvailable = this.props.data.some((lineChartData) => lineChartData.data.some((singleKpiData) => singleKpiData.isRate || false));
        const firstXAxisTickList = this.generateDailyTicks(1);
        const useAbsValue = visibleAttribute.some((singleVisibleAttribute) => singleVisibleAttribute.useAbsValue || false);
        const data = this.getData();

        return (
            <div style={{position: 'relative'}}>
                <RechartsLineChart
                    width={this.props.width}
                    height={this.props.height}
                    data={data}
                    onMouseDown={this.onDiagramMouseDown}
                    onMouseMove={this.onDiagramMouseMove}
                    onMouseUp={this.zoom}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        xAxisId="1"
                        allowDataOverflow={true}
                        dataKey="timestamp"
                        domain={[x1Range.start, x1Range.end]}
                        type="number"
                        ticks={firstXAxisTickList}
                        tickFormatter={(a: number) => this.formatXAxisTick(a)}
                    />
                    {compareDiagramUsed ? (
                        <XAxis
                            axisLine={false}
                            tickLine={false}
                            xAxisId="2"
                            allowDataOverflow={true}
                            dataKey="timestamp"
                            domain={[x2Range.start, x2Range.end]}
                            type="number"
                            ticks={this.generateDailyTicks(2)}
                            tick={{fill: GreyColor}}
                            tickFormatter={(a) => this.formatXAxisTick(a)}
                        />
                    ) : null}
                    {this.renderYAxis(useAbsValue, ratesAvailable)}
                    {this.props.disableTooltip ? null : (
                        <Tooltip
                            isAnimationActive={false}
                            content={
                                <TooltipContent
                                    lineChartData={this.props.data}
                                    kpiPackageToString={this.props.kpiPackageToString}
                                    visibleAttribute={this.props.visibleAttribute}
                                    payload={undefined}
                                    compareDateRange={this.props.compareDateRange}
                                    mainDateRange={this.props.mainDateRange}
                                    chartType={ChartType.LINE_CHART}
                                    tooltipComparisonModeIfOneElementIsSelected={this.props.tooltipComparisonModeIfOneElementIsSelected}
                                    currencyFormatting={this.currencyFormatting()}
                                />
                            }
                        />
                    )}
                    {this.renderLines(useAbsValue)}
                    {startDragTimestamp && endDragTimestamp ? (
                        <ReferenceArea yAxisId="1" xAxisId="1" x1={startDragTimestamp} x2={endDragTimestamp} strokeOpacity={0.3} />
                    ) : null}
                </RechartsLineChart>
                <div className={lineChartCss.lineTypeLegend}>{this.addNormalAndCompareLegenLines(compareDiagramUsed)}</div>
            </div>
        );
    }
}

const lineChartCss = stylesheet({
    lineTypeLegend: {position: 'absolute', bottom: 9, left: 5, color: DarkGreyColor},
    bottomLineTypeItem: {
        marginTop: 5,
    },
    axisLinePreview: {
        overflow: 'hidden',
        width: 24,
    },
});



// WEBPACK FOOTER //
// ./src/views/charts/LineChart.tsx